/**
 * Sprint-10 — edit an existing deployment after creation.
 *
 * Surfaces the same fields the New Deployment wizard does (name,
 * wave-irrelevant: not editable, opens/closes, individual reports
 * toggle, demographics) so consultants can correct typos or add new
 * roles without leaving the deployment.
 *
 * Reuses DemographicsSection from the wizard for consistency. The save
 * call hits PATCH /api/deployments/{id}/settings, which already
 * accepts demographic_config + name + opens_at + closes_at +
 * individual_reports_enabled (see deployments.SettingsUpdate).
 *
 * Editing is permitted on closed deployments too — consultants need to
 * fix demographics post-hoc when they spot errors after close.
 */
import React from "react";
import { api, formatApiError } from "../lib/api";
import DemographicsSection from "./DemographicsSection";

// Convert {role: [v1, v2, ...], team: [..]} -> [{_id, key, values:"v1\nv2"}, ...]
function configToRows(config) {
  const entries = Object.entries(config || {});
  if (entries.length === 0) {
    return [{ _id: "edit-init-0", key: "", values: "" }];
  }
  return entries.map(([k, vs], i) => ({
    _id: `edit-init-${i}`,
    key: k,
    values: (vs || []).join("\n"),
  }));
}

function rowsToConfig(rows) {
  const out = {};
  for (const row of rows) {
    const k = row.key.trim();
    if (!k) continue;
    const values = row.values
      .split(/[,\n;]+/)
      .map((v) => v.trim())
      .filter(Boolean);
    if (values.length === 0) continue;
    out[k] = values;
  }
  return out;
}

// Backend stores ISO 8601 (UTC); the datetime-local input wants
// YYYY-MM-DDTHH:MM in local time. Round-trip safely.
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function EditDeploymentModal({ deployment, onClose, onSaved }) {
  const [name, setName] = React.useState(deployment.name || "");
  const [demos, setDemos] = React.useState(() => configToRows(deployment.demographic_config));
  const [opensAt, setOpensAt] = React.useState(isoToLocalInput(deployment.opens_at));
  const [closesAt, setClosesAt] = React.useState(isoToLocalInput(deployment.closes_at));
  const [individualReports, setIndividualReports] = React.useState(
    Boolean(deployment.individual_reports_enabled),
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  function updateDemo(idx, field, value) {
    setDemos((d) => d.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  }
  function addDemoRow() {
    setDemos((d) => [
      ...d,
      { _id: `edit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, key: "", values: "" },
    ]);
  }
  function removeDemoRow(idx) {
    setDemos((d) => d.filter((_, i) => i !== idx));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Deployment name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        demographic_config: rowsToConfig(demos),
        individual_reports_enabled: individualReports,
        opens_at: opensAt ? new Date(opensAt).toISOString() : null,
        closes_at: closesAt ? new Date(closesAt).toISOString() : null,
      };
      const { data } = await api.patch(`/api/deployments/${deployment.id}/settings`, payload);
      onSaved(data.deployment);
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-12"
      style={{ background: "rgba(26,26,26,0.4)" }}
      data-testid="edit-deployment-modal"
      onClick={onClose}
    >
      <div
        className="bg-paper p-8 w-full max-w-2xl"
        style={{ border: "1px solid #DDDDDD" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow mb-2">Edit deployment</div>
        <h3 className="text-xl mb-1" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
          {deployment.name}
        </h3>
        <p className="text-sm text-gray-700 mb-6 max-w-prose">
          Update the deployment&rsquo;s name, schedule, and demographics. Adding a new
          role here makes it immediately available when inviting respondents. Existing
          respondents keep whatever demographic value they were invited with, even if
          you rename or remove a role.
        </p>

        {error && (
          <div className="mb-4 text-sm" style={{ color: "#AD393E" }} data-testid="edit-deployment-error">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="edit-deployment-name"
              style={{
                width: "100%", maxWidth: 420, padding: "10px 12px",
                border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Opens at <span className="text-gray-500 text-xs">(optional)</span></label>
              <input
                type="datetime-local"
                value={opensAt}
                onChange={(e) => setOpensAt(e.target.value)}
                data-testid="edit-deployment-opens-at"
                aria-label="Opens at (optional)"
                style={{
                  width: "100%", padding: "10px 12px",
                  border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                }}
              />
            </div>
            <div>
              <label className="label">Closes at <span className="text-gray-500 text-xs">(optional)</span></label>
              <input
                type="datetime-local"
                value={closesAt}
                onChange={(e) => setClosesAt(e.target.value)}
                data-testid="edit-deployment-closes-at"
                aria-label="Closes at (optional)"
                style={{
                  width: "100%", padding: "10px 12px",
                  border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                }}
              />
            </div>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={individualReports}
              onChange={(e) => setIndividualReports(e.target.checked)}
              data-testid="edit-deployment-individual-reports"
            />
            <span className="text-sm">Offer individual respondent reports at survey completion</span>
          </label>

          <DemographicsSection
            demos={demos}
            updateDemo={updateDemo}
            removeDemoRow={removeDemoRow}
            addDemoRow={addDemoRow}
          />

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="btn-quiet"
              data-testid="edit-deployment-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-deployment-save"
              style={{
                fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                fontSize: "11px", color: "#FBFAF7",
                background: saving ? "#999898" : "#1A1A1A",
                border: 0, padding: "10px 18px",
                cursor: saving ? "wait" : "pointer",
              }}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
